import { useEffect, useState } from 'react';
import azureDevOpsService from './azureDevOpsService';
import bugLinkService from './bugLinkService';
import { isFailedStatus } from '../utils/testStatus';

// Au-delà, on ne cherche pas : ~3 appels ADO par test en échec, et un run aussi
// rouge mérite de toute façon une revue humaine.
const MAX_FAILED_TO_CHECK = 50;
const CONCURRENCY = 5;

// Un run avec des échecs est « couvert » quand chacun de ses tests en échec a un
// bug lié — ouvert côté ADO (mêmes états que le détail de suite) ou lié dans l'app.
// Résultat partagé entre les cartes et les bandeaux, calculé une fois par run.
const coverage = {};
const pending = new Map();
const listeners = new Set();

export function coverageKey({ pipelineId, buildId, runId }) {
  return `${pipelineId}_${String(buildId).replace(/^build-/, '')}_${runId || ''}`;
}

function notify() {
  const snapshot = { ...coverage };
  listeners.forEach(fn => fn(snapshot));
}

async function mapWithConcurrency(items, fn) {
  const out = [];
  for (let i = 0; i < items.length; i += CONCURRENCY) {
    out.push(...await Promise.all(items.slice(i, i + CONCURRENCY).map(fn)));
  }
  return out;
}

async function failedTestNames({ buildId, runId }) {
  const numericBuildId = String(buildId).replace(/^build-/, '');
  // Carte RF : un run précis. Carte ReadyAPI : tous les runs du build.
  const runIds = runId
    ? [String(runId).replace(/^run-/, '')]
    : (await azureDevOpsService.getTestRunsByBuildId(numericBuildId)).map(r => r.id);
  const names = [];
  for (const id of runIds) {
    const results = await azureDevOpsService.fetchAllTestResults(id);
    results
      .filter(r => isFailedStatus(r.outcome))
      .forEach(r => names.push(r.testCaseTitle || r.automatedTestName));
  }
  return names.filter(Boolean);
}

async function localBugLinks(pipelineId, buildId) {
  // Les liens locaux sont indexés par le buildId tel que la route du détail l'a reçu,
  // avec ou sans préfixe « build- » selon la page : on regarde les deux.
  const numeric = String(buildId).replace(/^build-/, '');
  const maps = await Promise.all([numeric, `build-${numeric}`].map(id =>
    bugLinkService.getForBuild(pipelineId, id).catch(() => ({}))
  ));
  return Object.assign({}, ...maps);
}

async function computeCoverage(suite) {
  const names = await failedTestNames(suite);
  // Moins de noms que d'échecs annoncés = on n'a pas tout vu : pas de conclusion.
  if (names.length === 0 || names.length < suite.failed) return { failed: suite.failed, withBug: 0, allCovered: false };
  const local = await localBugLinks(suite.pipelineId, suite.buildId);
  const covered = await mapWithConcurrency(names, async name => {
    if (local[name]?.length) return true;
    const bugs = await azureDevOpsService.getRelatedBugsForTestCaseByTitle(name).catch(() => []);
    return bugs.length > 0;
  });
  const withBug = covered.filter(Boolean).length;
  return { failed: names.length, withBug, allCovered: withBug === names.length };
}

function request(suite) {
  if (!suite?.buildId || !(suite.failed > 0) || suite.failed > MAX_FAILED_TO_CHECK) return;
  const key = coverageKey(suite);
  if (coverage[key] || pending.has(key)) return;
  pending.set(key, computeCoverage(suite)
    .catch(err => {
      console.warn('Couverture des échecs par des bugs indisponible', key, err);
      return { failed: suite.failed, withBug: 0, allCovered: false };
    })
    .then(result => {
      coverage[key] = result;
      pending.delete(key);
      notify();
    }));
}

export function getCoverage(coverageMap, suite) {
  return coverageMap?.[coverageKey(suite)] || null;
}

/** Map clé -> { failed, withBug, allCovered } pour les suites en échec de la liste. */
export function useKnownFailures(suites) {
  const [snapshot, setSnapshot] = useState(() => ({ ...coverage }));
  const signature = (suites || [])
    .filter(s => s?.buildId && s.failed > 0)
    .map(s => `${coverageKey(s)}:${s.failed}`)
    .join('|');

  useEffect(() => {
    listeners.add(setSnapshot);
    return () => { listeners.delete(setSnapshot); };
  }, []);

  useEffect(() => {
    (suites || []).forEach(request);
    // `signature` résume `suites` : inutile de relancer à chaque nouveau tableau.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  return snapshot;
}
